import Vector from "victor";
import Rect from "@/wbcontroller/types/Rect";
import Camera from "@/wbcontroller/Camera";
import { SimpleShape2 } from "@/wbcontroller/standardShapes/StandardShapes";
import { shapeScale2, shapeBoundingRect2 } from "@/wbcontroller/util";

export interface SimpleShapeCoordinateSpaceDelta {
  translate: [number, number];
  pointA: [number, number];
  pointB: [number, number];
  boundingBoxOrigin: [number, number];
  boundingBoxSize: [number, number];
  boundingBoxOriginalSize: [number, number];
}

/**
 * For a "standard" two-point shape whose bounding rect covers the screen,
 * tighten its bounds and move its coordinates accordingly.
 */
export function getStandardShapeScreenToBoundedDelta(
  camera: Camera,
  shapePrototype: SimpleShape2,
  boundingBoxMargin: Vector,
  isAlreadyInWorldCoordinates = false
): SimpleShapeCoordinateSpaceDelta {
  if (isAlreadyInWorldCoordinates) {
    const worldSpaceRect = new Rect(
      Vector.fromArray(shapePrototype.pointA),
      Vector.fromArray(shapePrototype.pointB)
    ).inset(boundingBoxMargin.clone().multiplyScalar(-1));

    return {
      translate: [0, 0],
      pointA: Vector.fromArray(shapePrototype.pointA)
        .subtract(worldSpaceRect.min)
        .toArray() as [number, number],
      pointB: Vector.fromArray(shapePrototype.pointB)
        .subtract(worldSpaceRect.min)
        .toArray() as [number, number],
      boundingBoxOrigin: worldSpaceRect.min.toArray() as [number, number],
      boundingBoxSize: worldSpaceRect.size.toArray() as [number, number],
      boundingBoxOriginalSize: worldSpaceRect.size.toArray() as [
        number,
        number
      ],
    };
  } else {
    // Find the min point
    const screenSpaceMin = new Vector(
      Math.min(shapePrototype.pointA[0], shapePrototype.pointB[0]),
      Math.min(shapePrototype.pointA[1], shapePrototype.pointB[1])
    ).subtract(boundingBoxMargin);

    const screenSpaceMax = new Vector(
      Math.max(shapePrototype.pointA[0], shapePrototype.pointB[0]),
      Math.max(shapePrototype.pointA[1], shapePrototype.pointB[1])
    ).add(boundingBoxMargin);

    const screenSpaceRect = new Rect(screenSpaceMin, screenSpaceMax);
    const worldSpaceRect = new Rect(
      camera.convertScreenToWorld(screenSpaceMin),
      camera.convertScreenToWorld(screenSpaceMax)
    );

    return {
      translate: [0, 0],
      pointA: Vector.fromArray(shapePrototype.pointA)
        .subtract(screenSpaceMin)
        .toArray() as [number, number],
      pointB: Vector.fromArray(shapePrototype.pointB)
        .subtract(screenSpaceMin)
        .toArray() as [number, number],
      boundingBoxOrigin: worldSpaceRect.min.toArray() as [number, number],
      boundingBoxSize: worldSpaceRect.size.toArray() as [number, number],
      boundingBoxOriginalSize: screenSpaceRect.size.toArray() as [
        number,
        number
      ],
    };
  }
}

export function getStandardShapeBoundedToScreenDelta(
  camera: Camera,
  shapePrototype: SimpleShape2
): SimpleShapeCoordinateSpaceDelta {
  const startOriginVsWorld = shapeBoundingRect2(shapePrototype).min;
  const originOffset = camera.convertWorldToScreen(startOriginVsWorld);
  const shapeScale = shapeScale2(shapePrototype);
  // const finalScale = new Vector(camera.scale, camera.scale).divide(shapeScale);
  const finalScale = shapeScale.clone().divideScalar(camera.scale);

  return {
    translate: [0, 0],
    pointA: [
      originOffset.x + shapePrototype.pointA[0] / finalScale.x,
      originOffset.y + shapePrototype.pointA[1] / finalScale.y,
    ],
    pointB: [
      originOffset.x + shapePrototype.pointB[0] / finalScale.x,
      originOffset.y + shapePrototype.pointB[1] / finalScale.y,
    ],
    boundingBoxOrigin: camera.viewportRect.min.toArray() as [number, number],
    boundingBoxSize: camera.viewportRect.size.toArray() as [number, number],
    boundingBoxOriginalSize: camera.viewportSize.toArray() as [number, number],
  };
}
